/**
 * V105-7-2 round 2 (R2-H2): the runner release and the signer identity the hand-off commands pin,
 * from ONE source — release/gacpro-trust.json (generated into @aigovops/core as GACPRO_TRUST).
 * Copied text never carries a placeholder: when a value isn't published, callers say so instead.
 */
import { GACPRO_TRUST, gacActionPin, gacCliPin, type GacActionPin, type GacCliPin, type GacproTrust } from '@aigovops/core';

export interface RunnerRelease { archiveUrl: string; archiveSha256: string; imageRef: string | null; version: string | null }
export interface SignerPin { issuer: string; identity: string; nonProduction: boolean }

/** The pinned runner archive (+ image by digest) or null while the release isn't published. */
export function runnerRelease(t: GacproTrust = GACPRO_TRUST): RunnerRelease | null {
  const r = t.runner;
  if (!r.archive_url || !/^https:\/\//.test(r.archive_url) || !r.archive_sha256 || !/^[a-f0-9]{64}$/.test(r.archive_sha256)) return null;
  const imageRef = r.image && r.image_digest && /^sha256:[a-f0-9]{64}$/.test(r.image_digest) ? `${r.image}@${r.image_digest}` : null;
  return { archiveUrl: r.archive_url, archiveSha256: r.archive_sha256, imageRef, version: r.version };
}

/** The signer to pin: the one that signed this bundle, else the published production signer. */
export function signerPin(bundleSignerIdentity: string | null | undefined, t: GacproTrust = GACPRO_TRUST): SignerPin | null {
  const np = t.signer.non_production;
  if (bundleSignerIdentity) {
    const i = bundleSignerIdentity.indexOf('#');
    if (i > 0 && i < bundleSignerIdentity.length - 1) {
      const issuer = bundleSignerIdentity.slice(0, i); const identity = bundleSignerIdentity.slice(i + 1);
      return { issuer, identity, nonProduction: identity === np.identity && issuer === np.issuer };
    }
  }
  const p = t.signer.production;
  return p.identity ? { issuer: p.issuer, identity: p.identity, nonProduction: false } : null;
}

/** Round 3 (F-H2): a check can be issued and run only once the runner release and the production signer are published. */
export function runnerReady(t: GacproTrust = GACPRO_TRUST): boolean { return !!runnerRelease(t) && !!signerPin(null, t); }
/** Which part GAC Pro still has to publish (null when ready). */
export function runnerGap(t: GacproTrust = GACPRO_TRUST): 'runner' | 'signer' | null { return !runnerRelease(t) ? 'runner' : !signerPin(null, t) ? 'signer' : null; }

/**
 * V108-2-2 (ADR-0087 §1): the npm channel and the public Action, from the same trust config. Null until
 * published; callers then show the runner archive (above) instead of a range or `@latest`.
 * The verification hand-off keeps the sha256-pinned archive (V105-7-7 H-1): it holds the verification token.
 */
export function cliRelease(t: GacproTrust = GACPRO_TRUST): GacCliPin | null { return gacCliPin(t); }
export function actionRelease(t: GacproTrust = GACPRO_TRUST): GacActionPin | null { return gacActionPin(t); }
